import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { TransportDistribution as TransportDistributionType } from "@/types/transport-distribution/transport-distribution.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { TransportDistributionForm } from "./transport-distribution/TransportDistributionForm";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { useTransportDistributionTable } from "@/hooks/transport-distribution/useTransportDistributionTable";
import { useTransportDistributionColumns } from "./transport-distribution/columns";
import { useConfirmationDialog } from "@/hooks/transport-distribution/useConfirmationDialog";

export const TransportDistribution = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedDistribution, setSelectedDistribution] = useState<TransportDistributionType | null>(null);

  const {
    filteredDistributions,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    showArchived,
    setShowArchived,
    vehicleTypeFilters,
    setVehicleTypeFilters,
    clearFilters,
    vehicleTypeOptions,
  } = useTransportDistributionTable();

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog(refetch);

  const handleAddDistribution = () => {
    setSelectedDistribution(null);
    setFormOpen(true);
  };

  const handleEditDistribution = (distribution: TransportDistributionType) => {
    setSelectedDistribution(distribution);
    setFormOpen(true);
  };

  const handleArchiveAction = (distribution: TransportDistributionType) => {
    openDialog({
      type: distribution.isArchived ? "restore" : "archive",
      sourceId: distribution.id,
      sourceName: distribution.vehicleType,
    });
  };

  const columns = useTransportDistributionColumns(t, handleEditDistribution, handleArchiveAction);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("transportDistribution.filters.vehicleType"),
      options: vehicleTypeOptions,
      selectedValues: vehicleTypeFilters,
      onChange: setVehicleTypeFilters,
      showSearch: true,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("transportDistribution.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddDistribution}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("transportDistribution.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("transportDistribution.searchPlaceholder")}
        filters={filters}
        toggle={[
          {
            label: t("transportDistribution.showArchived"),
            checked: showArchived,
            onCheckedChange: setShowArchived,
          },
        ]}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={filteredDistributions} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <TransportDistributionForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        distributionToEdit={selectedDistribution}
        onSuccess={refetch}
      />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={handleConfirm}
          title={
            confirmAction.type === "archive"
              ? t("transportDistribution.confirmArchive.title")
              : t("transportDistribution.confirmRestore.title")
          }
          description={t(
            confirmAction.type === "archive"
              ? "transportDistribution.confirmArchive.description"
              : "transportDistribution.confirmRestore.description"
          )}
          confirmText={
            isConfirmActionLoading
              ? t(
                  confirmAction.type === "archive"
                    ? "transportDistribution.confirmArchive.actionLoading"
                    : "transportDistribution.confirmRestore.actionLoading"
                )
              : t(
                  confirmAction.type === "archive"
                    ? "transportDistribution.confirmArchive.action"
                    : "transportDistribution.confirmRestore.action"
                )
          }
          cancelText={t("transportDistribution.cancel")}
          variant={confirmAction.type === "archive" ? "destructive" : "default"}
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
